import { Injectable } from "@angular/core";
import { Subject, Observable } from "rxjs";
import {
  Firestore,
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  collectionData,
  deleteDoc,
  doc,
  updateDoc,
  getDocs,
  addDoc,
} from "@angular/fire/firestore";
import { HttpClient } from "@angular/common/http";

import { environment } from "../../environments/environment";
import {
  isIncomingUserChatMessage,
  playMessageArrival,
  rememberHeardChatMessageId,
} from "./cuelume-audio";
import { PinnedService } from "./main/accountant/pinned-panel/pinned.service";
import { DashboardStore } from "./dashboard.store";
import { MessageBubbleService } from "./conversations/message-bubble/message-bubble.service";
import { EventsService } from "./events.service";
import { WorkspacesService } from "./main/accountant/workspaces.service";
import {
  PinnedModels,
  PinnedModelsApps,
} from "./main/accountant/pinned-panel/pinned-models";
import { DashboardService } from "./dashboard.service";
import { NotificationsService } from "./notifications.service";

type CreatePinnedMessageModelResponse = {
  model_message_uuid: string;
  company_uuid: string;
};

@Injectable({ providedIn: "root" })
export class ChatService {
  clients = [];
  should_receive_events = false;
  isSubscribedToChats = false;
  isSubscribedToUpdates = false;
  activeConversationUuid = null;

  messagesAreLoaded = false;

  showBotMessages = new Subject();
  chatLoaded$ = new Subject();
  newMessage = new Subject();
  setReplyingTo = new Subject();

  currentChatSubscription = null;

  pinStatus = {
    unpinned: "unpinned",
    pinned: "pinned",
    completed: "completed",
  };

  constructor(
    private firestore: Firestore,
    private eventsService: EventsService,
    private pinnedService: PinnedService,
    public dashboardStore: DashboardStore,
    private http: HttpClient,
    private messageBubbleService: MessageBubbleService,
    private workspacesService: WorkspacesService,
    private notificationsService: NotificationsService,
  ) {
    this.dashboardStore.clientsLoaded.subscribe((sub: any) => {
      this.subscribeToChats(this.dashboardStore.clients);
    });
  }

  getLastElement(arr) {
    return arr[arr.length - 1];
  }

  getFirestoreMessages(uuid, lim = 100): Observable<any[]> {
    const messagesCollection = collection(
      this.firestore,
      `chats/${uuid}/messages`,
    );
    const messagesQuery = query(
      messagesCollection,
      orderBy("date", "desc"),
      limit(lim),
    );

    return new Observable((observer) => {
      const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
        const data = [];
        snapshot.docChanges().forEach((change) => {
          data.push({
            ...change.doc.data(),
            id: change.doc.id,
            type: change.type,
          });
        });

        observer.next(data.reverse());
      });

      return () => unsubscribe();
    });
  }

  updateConversationMessages(conversation, newMessages) {
    let wasAdded = false;
    const alreadyLoaded = conversation.company_conversation.loaded === true;
    let incomingUnreadCount = 0;
    let firstIncomingMessageId = null;
    this.formatDates(conversation.company_conversation.messages);
    conversation.company_conversation.messages = this.deduplicateMessages(
      conversation.company_conversation.messages,
    );
    this.formatDates(newMessages);

    for (let x = 0; x < newMessages.length; x++) {
      const messages = conversation.company_conversation.messages;
      const existingMessageIndex = messages.findIndex(
        (message) => message.id === newMessages[x].id,
      );

      if (newMessages[x].type == "added") {
        if (existingMessageIndex === -1) {
          messages.push(newMessages[x]);
          wasAdded = true;
          const isNewMessageId = rememberHeardChatMessageId(newMessages[x].id);
          if (
            alreadyLoaded &&
            isNewMessageId &&
            this.isIncomingChatMessage(newMessages[x])
          ) {
            incomingUnreadCount += 1;
            if (firstIncomingMessageId == null) {
              firstIncomingMessageId = newMessages[x].id;
            }
          }
        } else {
          messages[existingMessageIndex] = {
            ...messages[existingMessageIndex],
            ...newMessages[x],
            date: this.getPreferredMessageDate(
              messages[existingMessageIndex]?.date,
              newMessages[x].date,
            ),
          };
        }
      } else if (newMessages[x].type == "modified") {
        if (existingMessageIndex !== -1) {
          messages[existingMessageIndex] = {
            ...newMessages[x],
            date: this.getPreferredMessageDate(
              messages[existingMessageIndex]?.date,
              newMessages[x].date,
            ),
          };
        }
      } else if (newMessages[x].type == "removed") {
        conversation.company_conversation.messages =
          conversation.company_conversation.messages.filter(
            (m) => m.id !== newMessages[x].id,
          );
      }
    }

    if (wasAdded) {
      this.chatLoaded$.next({});
    }

    if (incomingUnreadCount > 0) {
      if (!this.notificationsService.preferences$.value.focus_active) {
        this.playIncomingMessageSound();
      }
      this.markIncomingConversationUnread(
        conversation,
        incomingUnreadCount,
        firstIncomingMessageId,
      );
    }

    this.messageBubbleService.togglePin();
    this.workspacesService.refreshPinnedMessages.next(null);
  }

  private isIncomingChatMessage(message): boolean {
    if (!isIncomingUserChatMessage(message, this.dashboardStore?.user?.id)) {
      return false;
    }

    const preferences = this.notificationsService.preferences$.value;
    const senderType = message?.sent_by?.user_type;
    if (senderType === 2) {
      return preferences.notify_client_messages;
    }
    if (senderType === 1) {
      return preferences.notify_staff_messages;
    }

    // Old payloads without sender type are ambiguous. Preserve the default
    // behavior, but do not violate either side of an explicit opt-out.
    return (
      preferences.notify_client_messages && preferences.notify_staff_messages
    );
  }

  private playIncomingMessageSound(): void {
    playMessageArrival();
  }

  private markIncomingConversationUnread(
    conversation,
    incomingMessageCount = 1,
    firstIncomingMessageId = null,
  ): void {
    const conversationId = conversation?.company_conversation?.id;
    if (conversationId == null) {
      return;
    }

    if (
      this.isViewingConversation(conversation) &&
      this.isWindowActive()
    ) {
      return;
    }

    this.dashboardStore.markConversationAsUnread(
      conversationId,
      incomingMessageCount,
      this.isViewingConversation(conversation) ? null : firstIncomingMessageId,
    );
  }

  private isViewingConversation(conversation): boolean {
    const activeUuid = this.dashboardStore?.activeConversationUUID;
    const incomingUuid = conversation?.company_conversation?.uuid;
    return (
      activeUuid != null && incomingUuid != null && activeUuid === incomingUuid
    );
  }

  private isWindowActive(): boolean {
    if (typeof document === "undefined") {
      return true;
    }

    return document.hidden !== true && document.hasFocus();
  }

  private deduplicateMessages(messages) {
    const uniqueMessages = [];
    const messageIndexes = new Map();

    messages.forEach((message) => {
      if (message.id == null) {
        uniqueMessages.push(message);
        return;
      }

      const existingMessageIndex = messageIndexes.get(message.id);

      if (existingMessageIndex == null) {
        messageIndexes.set(message.id, uniqueMessages.length);
        uniqueMessages.push(message);
        return;
      }

      const existingMessage = uniqueMessages[existingMessageIndex];
      uniqueMessages[existingMessageIndex] = {
        ...existingMessage,
        ...message,
        date: this.getPreferredMessageDate(existingMessage.date, message.date),
      };
    });

    return uniqueMessages;
  }

  private getPreferredMessageDate(existingDate, incomingDate) {
    if (this.isValidMessageDate(incomingDate)) {
      return incomingDate;
    }

    if (this.isValidMessageDate(existingDate)) {
      return existingDate;
    }

    return incomingDate ?? existingDate ?? null;
  }

  private isValidMessageDate(value) {
    if (value == null) {
      return false;
    }

    if (value instanceof Date) {
      return !Number.isNaN(value.getTime());
    }

    if (typeof value === "number" || typeof value === "string") {
      return !Number.isNaN(new Date(value).getTime());
    }

    return false;
  }

  changeConversationSubscription(conversation, lim = 100) {
    this.dashboardStore.changeConversationUUID(
      conversation.company_conversation.uuid,
    );

    if (conversation._sub != null) {
      conversation._sub.unsubscribe();
    }

    conversation.loading = true;
    conversation.company_conversation.messages = [];
    conversation.company_conversation.loaded = null;

    conversation._sub = this.getFirestoreMessages(
      conversation.company_conversation.uuid,
      lim,
    ).subscribe((sub: any) => {
      if (sub.length > 0) {
        // Handle modified or new messages
        this.updateConversationMessages(conversation, sub);

        // Format dates
        this.formatDates(conversation.company_conversation.messages);

        let update = this.getLastElement(
          conversation.company_conversation.messages,
        );

        this.dashboardStore.markOwnMessageAsRead(
          conversation.company_conversation.id,
          update,
        );

        this.eventsService.handleEvent(
          update,
          conversation.company_conversation,
        );

        if (
          conversation.company_conversation.loaded == undefined ||
          conversation.company_conversation.loaded == null
        ) {
          conversation.company_conversation.loaded = true;
        }

        if (sub.filter((m) => m.type == "added").length > 0) {
          this.chatLoaded$.next(conversation.company_conversation.uuid);
        }
      }

      conversation.loading = false;
    });
  }

  subscribeToChats(conversations) {
    console.log("subscribeToChats", conversations);
    conversations.forEach((c) => {
      if (c.type === "group") {
        return;
      }

      // clientsLoaded can fire again when the workspace collection changes.
      // Reusing the live subscription prevents the latest Firestore document
      // from being appended a second time by an orphaned listener.
      if (c._sub != null && c._sub.closed !== true) {
        return;
      }

      c._sub = this.getFirestoreMessages(
        c.company_conversation.uuid,
        1,
      ).subscribe((sub: any) => {
        if (sub.length > 0) {
          for (let x = 0; x < conversations.length; x++) {
            // Find the conversation it belongs to
            if (conversations[x].type === "group") {
              continue;
            }
            if (
              conversations[x].company_conversation.uuid ==
              c.company_conversation.uuid
            ) {
              this.updateConversationMessages(conversations[x], sub);

              let update = this.getLastElement(
                conversations[x].company_conversation.messages.filter(
                  (m) => m.type == "added",
                ),
              );

              if (update != null) {
                this.dashboardStore.markOwnMessageAsRead(
                  conversations[x].company_conversation.id,
                  update,
                );
              }

              if (
                conversations[x].company_conversation.loaded == undefined ||
                conversations[x].company_conversation.loaded == null
              ) {
                conversations[x].company_conversation.loaded = true;
              }

              // this.chatLoaded.next(c.company_conversation.uuid);
            }
          }
        }

        if (!this.messagesAreLoaded) this.messagesAreLoaded = true;
      });
    });

    this.isSubscribedToChats = true;
  }

  formatDates(messages) {
    messages.forEach((m) => {
      m.date = this.normalizeMessageDate(m.date);

      if (m.replyingTo != null) {
        m.replyingTo.date = this.normalizeMessageDate(m.replyingTo.date);
      }
    });
  }

  private normalizeMessageDate(value) {
    if (value == null || value instanceof Date) {
      return value;
    }

    if (typeof value === "object") {
      if (this.isFunction(value.toDate)) {
        return value.toDate();
      }

      const seconds = value.seconds ?? value._seconds;
      const nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0;

      if (Number.isFinite(seconds) && Number.isFinite(nanoseconds)) {
        return new Date(seconds * 1000 + nanoseconds / 1_000_000);
      }
    } else if (typeof value === "number") {
      return new Date(value);
    }

    return value;
  }

  isFunction(functionToCheck) {
    return (
      functionToCheck &&
      {}.toString.call(functionToCheck) === "[object Function]"
    );
  }

  getFirestoreServerDate() {
    return serverTimestamp();
  }

  /**
   * Explore: write a Sample Client AI reply into the same Firestore DB the chat reads.
   * Used when the backend Admin SDK posts to a different database (or fails).
   */
  async postExploreSampleClientReply(
    conversationUuid: string,
    reply: string,
    sentBy: {
      full_name: string;
      id: number | null;
      email: string;
      user_type?: number;
    },
  ): Promise<boolean> {
    if (!conversationUuid || !reply?.trim()) {
      return false;
    }

    try {
      await addDoc(
        collection(this.firestore, `chats/${conversationUuid}/messages`),
        {
          message: reply.trim(),
          tracked_item: null,
          system_message: null,
          file: null,
          reminder: false,
          sent_by: {
            full_name: sentBy?.full_name || "Sample Client",
            id: sentBy?.id ?? null,
            email: sentBy?.email || "sample.client@clienthub.app",
            user_type: sentBy?.user_type ?? 2,
          },
          replyingTo: null,
          date: this.getFirestoreServerDate(),
        },
      );
      return true;
    } catch (err) {
      console.error("[Explore] Failed to post Sample Client AI reply", err);
      return false;
    }
  }

  getChatByConversationUUID(conversation_uuid) {
    for (let x = 0; x < this.dashboardStore.clients.length; x++) {
      if (this.dashboardStore.clients[x].type === "group") {
        continue;
      }
      if (
        this.dashboardStore.clients[x].company_conversation.uuid ==
          conversation_uuid ||
        this.dashboardStore.clients[x].id == conversation_uuid
      ) {
        return this.dashboardStore.clients[x].company_conversation;
      }
    }
    return null;
  }

  getClientByConversationUUID(conversation_uuid) {
    for (let x = 0; x < this.dashboardStore.clients.length; x++) {
      const client = this.dashboardStore.clients[x];
      if (!client) continue;
      if (client.id == conversation_uuid) {
        const conversationUuid = client.company_conversation?.uuid;
        if (conversationUuid) {
          this.dashboardStore.activeConversationUUID = conversationUuid;
        }
        return client;
      }
      const cc = client.company_conversation;
      if (!cc || !cc.uuid) continue;
      if (cc.uuid == conversation_uuid) {
        this.dashboardStore.activeConversationUUID = cc.uuid;
        return client;
      }
    }
    return null;
  }

  async changeReactions(message: any, conversationUuid: string) {
    const messageRef = doc(
      this.firestore,
      `chats/${conversationUuid}/messages`,
      message.id,
    );

    try {
      await updateDoc(messageRef, {
        ...message,
      });
    } catch (error) {}
  }

  async editMessage(message: any, oldText: string, conversationUuid: string) {
    const history =
      message.history && Array.isArray(message.history) ? message.history : [];
    history.push({ timestamp: new Date().toISOString(), text: oldText });

    const messageRef = doc(
      this.firestore,
      `chats/${conversationUuid}/messages`,
      message.id,
    );

    try {
      await updateDoc(messageRef, {
        message: message.message,
        edited: true,
        last_edited: this.getFirestoreServerDate(),
        history: history,
      });

      message.edit_mode = false;
    } catch (error) {
      console.error("Error updating message:", error);
    }
  }

  async updateMessageWithJobInfo(
    messageId: string,
    conversationUuid: string,
    job: any,
  ) {
    if (!messageId || !conversationUuid) {
      console.warn(
        "Cannot update message: missing message ID or conversation UUID",
      );
      return;
    }

    try {
      const messageRef = doc(
        this.firestore,
        `chats/${conversationUuid}/messages`,
        messageId,
      );

      await updateDoc(messageRef, {
        job_uuid: job.uuid,
        related_job: {
          uuid: job.uuid,
          name: job.name,
          status: job.status,
        },
        job_created_at: new Date().toISOString(),
      });

      console.log("Message updated with job information");
    } catch (error) {
      console.error("Error updating message with job info:", error);
    }
  }

  async removeJobInfoFromMessage(messageId: string, conversationUuid: string) {
    if (!messageId || !conversationUuid) {
      console.warn(
        "Cannot update message: missing message ID or conversation UUID",
      );
      return;
    }

    try {
      const messageRef = doc(
        this.firestore,
        `chats/${conversationUuid}/messages`,
        messageId,
      );

      await updateDoc(messageRef, {
        job_uuid: null,
        related_job: null,
        job_created_at: null,
      });

      console.log("Job information removed from message");
    } catch (error) {
      console.error("Error removing job info from message:", error);
    }
  }

  // Public method to clean up job relationships from messages
  async cleanupJobFromMessages(
    jobUuid: string,
    conversationUuid: string,
  ): Promise<void> {
    return this.removeJobInfoFromAllMessages(jobUuid, conversationUuid);
  }

  async removeJobInfoFromAllMessages(
    jobUuid: string,
    conversationUuid: string,
  ) {
    if (!jobUuid || !conversationUuid) {
      console.warn(
        "Cannot remove job info: missing job UUID or conversation UUID",
      );
      return;
    }

    try {
      console.log(
        `ChatService: Starting to remove job info for job ${jobUuid} from conversation ${conversationUuid}`,
      );
      console.log(
        `ChatService: Firestore path: chats/${conversationUuid}/messages`,
      );
      console.log(
        `ChatService: Job UUID type: ${typeof jobUuid}, value: ${jobUuid}`,
      );
      console.log(
        `ChatService: Conversation UUID type: ${typeof conversationUuid}, value: ${conversationUuid}`,
      );

      // First, let's check if the conversation document exists
      try {
        const conversationRef = doc(this.firestore, `chats`, conversationUuid);
        console.log(
          `ChatService: Conversation document path: chats/${conversationUuid}`,
        );
      } catch (error) {
        console.log(
          `ChatService: Error checking conversation document:`,
          error,
        );
      }

      // Get all messages in the conversation
      const messagesCollection = collection(
        this.firestore,
        `chats/${conversationUuid}/messages`,
      );

      const messagesQuery = query(messagesCollection);
      const messagesSnapshot = await getDocs(messagesQuery);

      console.log(
        `ChatService: Found ${messagesSnapshot.size} messages in conversation`,
      );

      // Log all message IDs and their data for debugging
      const messageIds = [];
      const messageData = [];
      messagesSnapshot.forEach((docSnapshot) => {
        messageIds.push(docSnapshot.id);
        const data = docSnapshot.data();
        messageData.push({
          id: docSnapshot.id,
          job_uuid: data.job_uuid,
          related_job: data.related_job,
          from_job: data.from_job,
          message: data.message
            ? data.message.substring(0, 50) + "..."
            : "No message text",
        });
      });
      console.log(`ChatService: Message IDs found:`, messageIds);
      console.log(`ChatService: Message data found:`, messageData);

      const updatePromises = [];

      messagesSnapshot.forEach((docSnapshot) => {
        const messageData = docSnapshot.data();
        console.log(`ChatService: Checking message ${docSnapshot.id}:`, {
          job_uuid: messageData.job_uuid,
          related_job: messageData.related_job,
          from_job: messageData.from_job,
        });

        // Check for job relationship with flexible matching
        const hasJobRelationship =
          messageData.job_uuid === jobUuid ||
          (messageData.related_job &&
            messageData.related_job.uuid === jobUuid) ||
          messageData.from_job === jobUuid ||
          (messageData.related_job && messageData.related_job.id === jobUuid);

        if (hasJobRelationship) {
          console.log(
            `ChatService: Message ${docSnapshot.id} matches job ${jobUuid}, adding to cleanup`,
          );
          const messageRef = doc(
            this.firestore,
            `chats/${conversationUuid}/messages`,
            docSnapshot.id,
          );
          updatePromises.push(
            updateDoc(messageRef, {
              job_uuid: null,
              related_job: null,
              job_created_at: null,
            }),
          );
        }
      });

      console.log(
        `ChatService: Updating ${updatePromises.length} messages in Firestore`,
      );
      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
        console.log(
          `ChatService: Successfully updated ${updatePromises.length} messages in Firestore`,
        );
      } else {
        console.log(
          `ChatService: No messages found with job relationship to ${jobUuid}`,
        );
      }
    } catch (error) {
      console.error(
        "ChatService: Error removing job info from messages:",
        error,
      );
      console.error("ChatService: Error details:", error.message);
      console.error("ChatService: Error stack:", error.stack);
    }
  }

  async deleteMessage(message, conversation_uuid) {
    const messageRef = doc(
      this.firestore,
      `chats/${conversation_uuid}/messages`,
      message.id,
    );

    try {
      await deleteDoc(messageRef);
      console.log("Message deleted successfully");
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  }

  getAllHistory(conversation_uuid) {
    this.dashboardStore;
    return;
  }

  createPinnedMessageModel(message): Promise<CreatePinnedMessageModelResponse> {
    const url = `${environment.apiUrl}/conversations/pin/message`;
    return this.http
      .post<CreatePinnedMessageModelResponse>(url, {
        message,
        message_id: message.id || message.firestore_message_id,
      })
      .toPromise();
  }

  async pinMessageOnFirestore(
    conversation_uuid: string,
    messageId: string,
    pinStatus: string,
  ) {
    console.log(
      "pinMessageOnFirestore",
      conversation_uuid,
      messageId,
      pinStatus,
    );

    const messageRef = doc(
      this.firestore,
      `chats/${conversation_uuid}/messages`,
      messageId,
    );

    try {
      await updateDoc(messageRef, {
        pinned: pinStatus,
      });

      return pinStatus;
    } catch (error) {
      console.error("Error updating pin status:", error);
      return null; // Handle the error as needed
    }
  }

  async pinMessage(
    message,
    conversation_uuid,
    company_uuid,
    manualStatus: "pinned" | "completed" | "unpinned" | null = null,
  ) {
    let pinStatus = null;

    if (message.pinned == true || message.pinned == this.pinStatus.pinned) {
      pinStatus = this.pinStatus.completed;
      message.pinned = pinStatus;
    } else if (
      message.pinned == false ||
      message.pinned == this.pinStatus.unpinned ||
      message.pinned == null
    ) {
      pinStatus = this.pinStatus.pinned;
      message.pinned = pinStatus;
    } else if (message.pinned == this.pinStatus.completed) {
      pinStatus = this.pinStatus.unpinned;
      message.pinned = pinStatus;
    }

    if (manualStatus !== null) {
      pinStatus = manualStatus;
    }

    // Create message model
    const { model_message_uuid } = await this.createPinnedMessageModel(message);

    // Pin message model
    const data = {
      model_name: PinnedModels.Message,
      model_app_name: PinnedModelsApps.Message,
      object_pk: model_message_uuid,
    };

    const { item_uuid } = await this.pinnedService.pinItem(data, company_uuid);

    message.pinUuid = item_uuid;

    // Save pin uuid in firestore
    const messageRef = doc(
      this.firestore,
      `chats/${conversation_uuid}/messages`,
      message.id,
    );

    try {
      await updateDoc(messageRef, {
        pinUuid: item_uuid,
      });
    } catch (error) {
      console.error("Error setting pinUuid for the message:", error);
      // Handle the error as needed
    }

    // Pin on firestore
    this.pinMessageOnFirestore(conversation_uuid, message.id, pinStatus);

    return pinStatus;
  }

  async toggleMessageVisibility(message: any, conversation_uuid: string) {
    const messageRef = doc(
      this.firestore,
      `chats/${conversation_uuid}/messages`,
      message.id,
    );

    try {
      await updateDoc(messageRef, {
        deleted: !message.deleted,
      });

      return !message.deleted;
    } catch (error) {
      console.error("Error toggling message visibility:", error);
      return message.deleted; // Return the previous state in case of an error
    }
  }

  async markMessageAsRead(
    message: any,
    conversation_uuid: string,
  ): Promise<void> {
    const user = this.dashboardStore.user;
    if (!user || !user.id) {
      return;
    }

    if (message.sent_by?.id === user.id) {
      return;
    }

    if (!this.shouldMarkAsRead(message)) {
      return;
    }

    const readBy = message.read_by || [];
    const alreadyRead = readBy.some(
      (receipt: any) => receipt.user_id === user.id,
    );

    if (alreadyRead) {
      return;
    }

    const messageRef = doc(
      this.firestore,
      `chats/${conversation_uuid}/messages`,
      message.id,
    );

    const readReceipt = {
      name: user.full_name,
      read_on: new Date().toISOString(),
      pictureUrl: user.pictureUrl,
      user_id: user.id,
    };

    try {
      await updateDoc(messageRef, {
        read_by: [...readBy, readReceipt],
      });
    } catch (error) {}
  }

  async markMessagesAsRead(
    messages: any[],
    conversation_uuid: string,
  ): Promise<void> {
    const user = this.dashboardStore.user;
    if (!user || !user.id) {
      return;
    }

    const readReceipt = {
      name: user.full_name,
      read_on: new Date().toISOString(),
      pictureUrl: user.pictureUrl || "",
      user_id: user.id,
    };

    const notOwnMessages = messages.filter((message) => {
      if (message.sent_by?.id === user.id) {
        return false;
      }
      return true;
    });

    const eligibleMessages = notOwnMessages.filter((message) =>
      this.shouldMarkAsRead(message),
    );

    const unreadMessages = eligibleMessages.filter((message) => {
      const readBy = message.read_by || [];
      return !readBy.some((receipt: any) => receipt.user_id === user.id);
    });

    const updates = unreadMessages.map((message) => {
      const messageRef = doc(
        this.firestore,
        `chats/${conversation_uuid}/messages`,
        message.id,
      );
      const readBy = message.read_by || [];

      return updateDoc(messageRef, {
        read_by: [...readBy, readReceipt],
      });
    });

    try {
      await Promise.all(updates);
    } catch (error) {}
  }

  private shouldMarkAsRead(message: any): boolean {
    const user = this.dashboardStore.user;

    if (!message.id) {
      return false;
    }

    if (message.sent_by?.id === user?.id) {
      return false;
    }

    if (message.system_message === true) {
      return false;
    }

    if (message.reminder === true) {
      return false;
    }

    if (message.tracked_item !== null && message.tracked_item !== undefined) {
      return false;
    }

    return true;
  }
}
